import React from "react";
import "./SignGraphics.css";
import StatisticsSection from "./StatisticsSection";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../constants/seo";
import { useSignContent } from "../hooks/useSignContent";
import { resolveAssetUrl } from "../utils/assets";
import { HomePageSkeleton } from "../components/Skeleton";

const SignGraphics = () => {
  const { content, loading } = useSignContent();
  const { hero, about, services, achievements, work, clients, cta } = content;

  const heroBg = resolveAssetUrl(hero.backgroundImage) || hero.backgroundImage;

  const handleSecondaryClick = (e) => {
    const link = hero.secondaryButtonLink || "#sign-services";
    if (link.startsWith("#")) {
      e.preventDefault();
      document.querySelector(link)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <main className="sign-page">
        <Seo {...PAGE_SEO.signGraphics} />
        <HomePageSkeleton />
      </main>
    );
  }

  return (
    <main className="sign-page">
      <Seo {...PAGE_SEO.signGraphics} />

      <section
        className="sign-hero"
        style={heroBg ? { backgroundImage: `url(${heroBg})` } : undefined}
      >
        <div className="sign-hero-content">
          {hero.badge && <span className="sign-badge">{hero.badge}</span>}
          {hero.title && <h1>{hero.title}</h1>}
          {hero.subtitle && <p>{hero.subtitle}</p>}

          <div className="sign-buttons">
            {hero.primaryButtonText && (
              <a href={hero.primaryButtonLink || "#"} className="primary-btn">
                {hero.primaryButtonText}
              </a>
            )}
            {hero.secondaryButtonText && (
              <a
                href={hero.secondaryButtonLink || "#sign-services"}
                className="secondary-btn"
                onClick={handleSecondaryClick}
              >
                {hero.secondaryButtonText}
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="sign-about">
        <div className="sign-about-text">
          {about.label && <span className="section-label">{about.label}</span>}
          {about.title && <h2>{about.title}</h2>}
          {(about.paragraphs || []).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <div className="sign-about-card">
          {about.cardTitle && <h3>{about.cardTitle}</h3>}
          {about.cardDescription && <p>{about.cardDescription}</p>}
        </div>
      </section>

      <section className="sign-services" id="sign-services">
        <div className="section-heading">
          {services.subtitle && (
            <span className="section-label">{services.subtitle}</span>
          )}
          {services.title && <h2>{services.title}</h2>}
          {services.description && <p>{services.description}</p>}
        </div>

        <div className="services-grid">
          {(services.items || []).map((service, index) => (
            <div className="service-card" key={service._id || index}>
              <div className="service-number">
                {String(index + 1).padStart(2, "0")}
              </div>
              <h3>{service.title}</h3>
              {service.description && <p>{service.description}</p>}
            </div>
          ))}
        </div>
      </section>

      <StatisticsSection data={achievements} />

      <section className="portfolio-preview">
        <div className="section-heading">
          {work.subtitle && <span className="section-label">{work.subtitle}</span>}
          {work.title && <h2>{work.title}</h2>}
          {work.description && <p>{work.description}</p>}
        </div>

        <div className="portfolio-gallery">
          {(work.items || []).map((item, index) => {
            const src = resolveAssetUrl(item.image) || item.image;
            if (!src) return null;
            return (
              <div className="portfolio-image-card" key={item._id || index}>
                <img src={src} alt={item.alt || `Work ${index + 1}`} />
              </div>
            );
          })}
        </div>
      </section>

      <section className="clients-section">
        <div className="section-heading">
          {clients.subtitle && (
            <span className="section-label">{clients.subtitle}</span>
          )}
          {clients.title && <h2>{clients.title}</h2>}
        </div>

        <div className="clients-grid">
          {(clients.items || []).map((client, index) => {
            const logo = resolveAssetUrl(client.image) || client.image;
            return (
              <div className="client-box" key={client._id || index}>
                {logo ? (
                  <img src={logo} alt={client.title || `Client ${index + 1}`} />
                ) : (
                  client.title
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="sign-cta">
        {cta.title && <h2>{cta.title}</h2>}
        {cta.description && <p>{cta.description}</p>}
        {cta.buttonText && (
          <a href={cta.buttonLink || "#"} className="primary-btn">
            {cta.buttonText}
          </a>
        )}
      </section>
    </main>
  );
};

export default SignGraphics;
